import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import FleetIcon from '../fleet/lorry/FleetIcon';
import { CargoMap } from '../fleet/lorry/FleetMap';
import '../site-v3/legacy-v3.css'; // Barlow, the trasseapp.com face (2026-10-28)

type PublicTrip = {
  status?: string;
  cargoRef?: string;
  goodsDescription?: string;
  origin?: string;
  destination?: string;
  vehiclePlate?: string;
  driverName?: string;
  distanceKm?: number;
  totalDistanceKm?: number;
  speedKmh?: number;
  trackingLinkExpiresAt?: { toDate?: () => Date };
  routeStartedAt?: { toDate?: () => Date };
  updatedAt?: { toDate?: () => Date };
};

function formatEtaMinutes(mins: number): string {
  const m = Math.max(1, Math.round(mins));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** Public cargo tracking — design: screen-owner.jsx. No login required; link expires when trip completes. */
const PublicTrack: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [trip, setTrip] = useState<PublicTrip | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setMissing(true);
      return;
    }
    const ref = doc(db, 'publicTripTrack', token);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setTrip(null);
          setMissing(true);
        } else {
          setTrip(snap.data() as PublicTrip);
          setMissing(false);
        }
        setLoading(false);
      },
      () => {
        setTrip(null);
        setMissing(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [token]);

  const expired = useMemo(() => {
    if (!trip) return false;
    const exp = trip.trackingLinkExpiresAt?.toDate?.();
    if (exp && exp < new Date()) return true;
    const st = String(trip.status ?? 'active');
    return st === 'completed' || st === 'cancelled';
  }, [trip]);

  const progress = useMemo(() => {
    const travelled = Number(trip?.distanceKm ?? 0);
    const total = Number(trip?.totalDistanceKm ?? 0);
    if (total <= 0) return 0.1;
    return Math.min(0.96, Math.max(0.05, travelled / total));
  }, [trip?.distanceKm, trip?.totalDistanceKm]);

  const km = Number(trip?.distanceKm ?? 0);
  const totalKm = Number(trip?.totalDistanceKm ?? 0);
  const remaining = Math.max(0, totalKm - km);
  const avgSpeed = Number(trip?.speedKmh ?? 0) > 0 ? Number(trip?.speedKmh) : 58;
  const etaMins = totalKm > 0 && remaining > 0 ? (remaining / avgSpeed) * 60 : 0;
  const inTransit = !expired && (trip?.status === 'active' || trip?.status === 'in_progress' || !trip?.status);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6F5F1', fontFamily: 'Barlow, system-ui, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5D646B' }}>
        Loading live tracking…
      </div>
    );
  }

  if (missing || !trip) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6F5F1', fontFamily: 'Barlow, system-ui, sans-serif', padding: 40 }}>
        <div style={{ maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: '#E2F5F8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <FleetIcon name="route" size={26} color="#00707F" />
          </div>
          <h1 style={{ fontSize: 20, color: '#0F1419', margin: '0 0 8px' }}>Tracking link not found</h1>
          <p style={{ color: '#5D646B', fontSize: 14, lineHeight: 1.5 }}>This link may be invalid or the trip has not started yet. Ask your fleet for a fresh link or sign in to the Pear app with your client account.</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6F5F1', fontFamily: 'Barlow, system-ui, sans-serif', padding: 40 }}>
        <div style={{ maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: '#E4F1E9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <FleetIcon name="check" size={26} color="#1E7A4C" />
          </div>
          <h1 style={{ fontSize: 20, color: '#0F1419', margin: '0 0 8px' }}>Delivery completed</h1>
          <p style={{ color: '#5D646B', fontSize: 14 }}>This tracking link has expired. Cargo ref <strong>{trip.cargoRef || '—'}</strong> is no longer in transit.</p>
        </div>
      </div>
    );
  }

  const plate = trip.vehiclePlate || 'Vehicle';
  const speed = Math.round(Number(trip.speedKmh ?? 64));
  const dest = trip.destination || 'Destination';
  const origin = trip.origin || 'Origin';
  const cargoTitle = trip.goodsDescription || 'Your shipment';
  const cargoRef = trip.cargoRef || 'CG-—';

  return (
    <div style={{ minHeight: '100vh', background: '#F6F5F1', fontFamily: 'Barlow, system-ui, sans-serif', color: '#0F1419', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', maxWidth: 480, margin: '0 auto', width: '100%' }}>
        <CargoMap height={372} progress={progress} />

        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '48px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(15,20,25,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FleetIcon name="truck" size={18} color="#0F1419" />
          </div>
          <div style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(15,20,25,0.1)', borderRadius: 14, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: 3.5, background: inTransit ? '#22D17E' : '#8C9196', boxShadow: inTransit ? '0 0 6px #22D17E' : undefined }} />
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0F1419' }}>{inTransit ? 'Cargo in transit' : 'Delivered'}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (token) navigator.clipboard.writeText(window.location.href).catch(() => {});
            }}
            style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(15,20,25,0.1)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <FleetIcon name="share" size={16} color="#0F1419" />
          </button>
        </div>

        <div style={{ position: 'absolute', top: 150, left: '54%', transform: 'translate(-50%, -120%)', background: '#0F1419', color: '#fff', padding: '6px 11px', borderRadius: 10, fontSize: 11, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap', boxShadow: '0 8px 20px -6px rgba(0,0,0,0.45)' }}>
          {plate} · {speed} km/h
          <div style={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: 8, height: 8, background: '#0F1419' }} />
        </div>

        <div style={{ position: 'absolute', left: 16, bottom: 44, background: 'rgba(15,27,45,0.92)', color: '#fff', borderRadius: 14, padding: '10px 13px', boxShadow: '0 8px 20px -8px rgba(0,0,0,0.5)' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', letterSpacing: 0.6, textTransform: 'uppercase' }}>Distance travelled</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>{km.toFixed(1)}</span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>/ {totalKm.toFixed(0)} km</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: -30, background: '#F6F5F1', borderRadius: '26px 26px 0 0', flex: 1, padding: '12px 18px 32px', position: 'relative', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', width: '100%' }}>
        <div style={{ width: 42, height: 4, borderRadius: 2, background: '#D9E4F1', margin: '0 auto 14px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, background: '#E2F5F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FleetIcon name="box" size={22} color="#00707F" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F1419' }}>{cargoTitle}</div>
            <div style={{ fontSize: 12, color: '#5D646B', marginTop: 2 }}>
              Manifest <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#0F1419' }}>#{cargoRef}</span>
            </div>
          </div>
          <div style={{ fontSize: 9, padding: '4px 8px', background: '#E4F1E9', color: '#1E7A4C', borderRadius: 6, fontWeight: 700, letterSpacing: 0.5 }}>ON TIME</div>
        </div>

        <div style={{ marginTop: 14, background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #00707F, #00C9F0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 6px 16px -6px rgba(0,158,190,0.7)' }}>
              <FleetIcon name="clock" size={20} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#00707F', letterSpacing: 1, textTransform: 'uppercase' }}>Arriving {dest.split(',')[0]} in</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0F1419', fontFamily: 'JetBrains Mono, monospace', letterSpacing: -1 }}>{formatEtaMinutes(etaMins)}</div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: '12px 0 0', borderTop: '1px dashed #E4E3DE', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            {[
              { label: origin.split(',')[0] || 'Start', state: 'done' as const },
              { label: 'En route', state: 'live' as const },
              { label: dest.split(',')[0] || 'Dest', state: 'dest' as const },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', height: 16, alignItems: 'center' }}>
                  {s.state === 'done' && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#22D17E' }} />}
                  {s.state === 'live' && (
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: -5, borderRadius: 14, background: 'rgba(0,158,190,0.25)' }} />
                      <div style={{ position: 'relative', width: 14, height: 14, borderRadius: 7, background: '#00707F', border: '2px solid #fff' }} />
                    </div>
                  )}
                  {s.state === 'dest' && <FleetIcon name="flag" size={12} color="#8C9196" />}
                </div>
                <div style={{ fontSize: 10, fontWeight: s.state === 'live' ? 700 : 500, color: s.state === 'live' ? '#00707F' : '#5D646B', marginTop: 6 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
          {[
            { l: 'Travelled', v: `${km.toFixed(0)} km`, ic: 'route' },
            { l: 'Avg speed', v: `${avgSpeed.toFixed(0)} km/h`, ic: 'gauge' },
            { l: 'Remaining', v: `${remaining.toFixed(0)} km`, ic: 'pin' },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid #E4E3DE', borderRadius: 16, padding: '12px 13px' }}>
              <FleetIcon name={s.ic} size={15} color="#00707F" />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F1419', marginTop: 8, fontFamily: 'JetBrains Mono, monospace' }}>{s.v}</div>
              <div style={{ fontSize: 9.5, color: '#5D646B', marginTop: 2, letterSpacing: 0.4, textTransform: 'uppercase' }}>{s.l}</div>
            </div>
          ))}
        </div>

        {trip.driverName && (
          <div style={{ marginTop: 10, background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: '#FFCBA4', color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {trip.driverName.slice(0, 2).toUpperCase()}
              </div>
              <div style={{ position: 'absolute', right: -2, bottom: -2, width: 14, height: 14, borderRadius: 7, background: '#22D17E', border: '2.5px solid #fff' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1419' }}>{trip.driverName}</div>
              <div style={{ fontSize: 11.5, color: '#5D646B', marginTop: 2 }}>
                Driving <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#0F1419' }}>{plate}</span>
              </div>
            </div>
          </div>
        )}

        <p style={{ marginTop: 20, fontSize: 12, color: '#5D646B', textAlign: 'center', lineHeight: 1.5 }}>
          Prefer the app? Sign in with your Pear client account (e.g. your fleet-assigned test email) for chat and history.
        </p>
        <a href="https://pearapp.com" style={{ display: 'block', marginTop: 12, padding: '12px 20px', background: '#00707F', color: '#fff', borderRadius: 12, textDecoration: 'none', fontWeight: 600, textAlign: 'center' }}>
          Download Pear app
        </a>
      </div>
    </div>
  );
};

export default PublicTrack;
